import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import {
  createConversation,
  createConversationChannel,
  getConversations,
  getConversation,
  createConversationHandover,
  updateConversation,
  makeConversationRead,
  deleteConversation,
} from './conversations.service';
import {
  Conversation,
  GetConversationsParams,
  ConversationHandoverData,
  UpdateConversationBody,
} from './conversations.types';

import { OrgLocation } from '../organizations/organizations.types';
import { persistStorageService } from '../auth/persist-storage';

export const conversationsQueryKeys = {
  all: ['conversations'] as const,
  list: (params: GetConversationsParams) =>
    [
      ...conversationsQueryKeys.all,
      params.page,
      params.perPage,
      params?.sort,
      params?.order,
      params?.filter,
    ] as const,
  details: (id: string) => [...conversationsQueryKeys.all, id] as const,
};

const getConversationsQuery = (params: GetConversationsParams) => {
  const queryKey = conversationsQueryKeys.list(params);

  return queryOptions({
    queryKey,
    queryFn: () => getConversations(params),
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    gcTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useGetConversations = (
  params: GetConversationsParams & { enabled?: boolean },
) => {
  return useQuery({
    ...getConversationsQuery(params),
    select: (data) => data?.data,
    enabled: params.enabled ?? true,
  });
};

export const useGetConversation = (id: string | null | undefined) => {
  return useQuery({
    queryKey: conversationsQueryKeys.details(id || ''),
    queryFn: () => getConversation({ id: id! }),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000, // 5 minutes
    select: (data) => data?.data,
  });
};

// Create conversation mutation
export const useCreateConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      channel_id: string;
      phone_number: string;
      summary: string;
      has_agent_active: boolean;
      wab_chatId_external?: string;
      chatbot_id?: string;
      user_id?: string;
    }) => {
      // Get selected organization and location from localStorage
      const selectedOrg = persistStorageService.getPersistItem<OrgLocation>({
        name: 'selectedOrganisation',
        parse: true,
      });

      const selectedLoc = persistStorageService.getPersistItem<OrgLocation>({
        name: 'selectedLocation',
        parse: true,
      });

      // Validate required fields
      if (!selectedOrg?.id) {
        throw new Error('Organisation ID is required but not found in localStorage');
      }

      // Create the conversation payload - only include fields that have values
      const conversationPayload: any = {
        summary: data.summary,
        status: 'active',
        has_agent_active: data.has_agent_active,
        is_read: true,
        has_attachments: false,
        realtime: true,
        organisation_id: selectedOrg.id, // Required field
        last_message_at: new Date().toISOString(),
        start_date: new Date().toISOString(),
        end_date: null,
      };

      // Only add optional fields if they have values
      if (data.chatbot_id) conversationPayload.chatbot_id = data.chatbot_id;
      if (data.user_id) conversationPayload.user_id = data.user_id;
      if (selectedLoc?.id) conversationPayload.location_id = selectedLoc.id;
      if (data.wab_chatId_external) conversationPayload.wab_chatId_external = data.wab_chatId_external;

      console.log('[useCreateConversation] Creating conversation with payload:', conversationPayload);

      const response = await createConversation(conversationPayload);

      console.log('response createConversation', response);

      if (response && response.data) {
        // Create the conversation-channel mapping
        const conversationChannelResponse = await createConversationChannel({
          conversation_id: response.data.id,
          channel_id: data.channel_id,
        });

        return conversationChannelResponse;
      }

      return null;
    },
    onSuccess: async (data) => {
      if (data) {
        // Add the new conversation to the cache
        queryClient.setQueryData(
          conversationsQueryKeys.all,
          (old: { data: Conversation[]; total: number } | undefined) => {
            if (!old) return { data: [data], total: 1 };
            return {
              data: [data, ...old.data],
              total: old.total + 1,
            };
          },
        );
        await queryClient.invalidateQueries({
          queryKey: conversationsQueryKeys.all,
        });
      }
    },
  });
};

const agentStatusMutation = (conversation?: Conversation) => {
  return {
    mutationFn: async ({
      conversationId,
      hasAgentActive,
      handoverData,
    }: {
      conversationId: string;
      hasAgentActive: boolean;
      handoverData: Partial<ConversationHandoverData>;
    }) => {
      console.log('conversation agentStatusMutation **********', conversation);
      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // 1. Update the conversation's has_agent_active status
      await updateConversation({
        id: conversationId,
        data: { has_agent_active: hasAgentActive },
        previousData: conversation,
      });

      // 2. Create a handover record with UTC timestamp
      // WORKAROUND: Backend handover endpoint has timezone bug - it stores local time as UTC
      // So we need to send UTC time explicitly until backend is fixed
      const utcTimestamp = new Date().toISOString();
      const handoverRecord = {
        id: uuidv4(),
        conversation_id: conversationId,
        from_user_id: handoverData.from_user_id,
        to_user_id: handoverData.to_user_id,
        agent_id: hasAgentActive ? conversation.chatbot_id : null,
        handover_reason:
          handoverData.handover_reason ||
          (hasAgentActive ? 'Handover to AI agent' : 'Human takeover'),
        is_agent_handover: hasAgentActive,
        handover_status: handoverData.handover_status || 'completed',
        // Explicitly send UTC timestamp to match message timestamp format
        handover_time: utcTimestamp,
      };

      // 2. Create a handover record
      await createConversationHandover(handoverRecord);

      return true;
    },
  };
};

export const useConversationsUpdateAgentStatus = ({
  conversation,
  onSuccess,
}: {
  conversation?: Conversation;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...agentStatusMutation(conversation),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationsQueryKeys.all,
      });
      onSuccess?.();
    },
  });
};

// TODO: Reuse that instead of mutation on agents.
export const useUpdateConversation = (conversation: Conversation) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; data: UpdateConversationBody }) => {
      return updateConversation({
        id: data.id,
        data: data.data,
        previousData: conversation,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationsQueryKeys.details(conversation.id),
      });
    },
  });
};

export const useMakeConversationRead = (conversation: Conversation) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; data: UpdateConversationBody }) => {
      return makeConversationRead({
        id: data.id,
        data: data.data,
        previousData: conversation,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationsQueryKeys.all,
      });
    },
  });
};

export const useDeleteConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      return deleteConversation({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationsQueryKeys.all,
      });
    },
  });
};
